/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import lottie from "lottie-web";
import { cn } from "~/lib/utils";
import mapBackground from "~/assets/connecting-family/map-background.svg";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

const familyDriver = imageUrl("/images/connecting-family/family-driver.webp");

/**
 * Ported from bouncieweb's `LandingPageComponents/FamilyHero.js` and the sub-pieces
 * it composed: `HeroBackgroundContainer`, `WidthConstraintContainer`,
 * `LandingPageHeader` (H1), and `LottieAnimation` (the "amoeba" blob behind the
 * family driver image). Faithful reproduction of layout, copy, and the parallax
 * map background.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

const PARALLAX_TRAVEL = 150;

/**
 * Ported from bouncieweb's `hooks/useParallax`. Tracks the observed section's
 * height and computes a vertical offset (0..PARALLAX_TRAVEL) that decreases as the
 * section scrolls up, driving the background image's `object-position` Y value.
 */
function useParallax(
  parallaxTravel: number,
  parentRef: React.RefObject<HTMLElement | null>,
) {
  const [parentHeight, setParentHeight] = useState(0);
  const [childOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const node = parentRef.current;
    if (!node) return;

    // Set initial position on render
    setParentHeight(node.getBoundingClientRect().height);
    setParallaxOffset(parallaxTravel);

    const handleChange = () => {
      if (!parentRef.current) return;
      const parentSection = parentRef.current.getBoundingClientRect();
      const viewportToBottomOfParent = parentSection.top + parentSection.height;
      if (viewportToBottomOfParent < 0) {
        // Parent is scrolled off screen; no need to calculate
        return;
      }
      const windowToBottomOfParent = viewportToBottomOfParent + window.scrollY;
      const offset =
        parallaxTravel * (viewportToBottomOfParent / windowToBottomOfParent);
      setParallaxOffset(offset);
      setParentHeight(parentSection.height);
    };

    let animationTimeout: number | undefined;
    const animationFrameHandler = () => {
      if (animationTimeout) {
        window.cancelAnimationFrame(animationTimeout);
      }
      animationTimeout = window.requestAnimationFrame(handleChange);
    };
    window.addEventListener("resize", animationFrameHandler);
    window.addEventListener("scroll", animationFrameHandler);

    return () => {
      if (animationTimeout) window.cancelAnimationFrame(animationTimeout);
      window.removeEventListener("resize", animationFrameHandler);
      window.removeEventListener("scroll", animationFrameHandler);
    };
  }, [parentRef, parallaxTravel]);

  return { parentHeight, childOffset };
}

/**
 * Ported from `LottieAnimation` — the "amoeba" blob. Loaded by URL path inside a
 * useEffect so SSR never touches lottie.
 */
function AmoebaLottie({ className }: { className?: string }) {
  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!animationRef.current) return;
    const anim = lottie.loadAnimation({
      container: animationRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      path: "/landing/amoeba.json",
    });
    return () => anim.destroy();
  }, []);

  return <div ref={animationRef} className={className} />;
}

export default function FamilyHero() {
  // Observes the hero section for the parallax calc.
  const heroSectionRef = useRef<HTMLElement | null>(null);
  const { parentHeight: heroHeight, childOffset: roadBgOffset } = useParallax(
    PARALLAX_TRAVEL,
    heroSectionRef,
  );

  return (
    // HeroSection (styled.section): overflow-hidden, relative, full width
    <section ref={heroSectionRef} className="relative w-full overflow-hidden">
      {/* HeroBackgroundContainer: parallax map, masked top+bottom fade */}
      <div
        className="absolute bottom-0 left-0 w-full overflow-hidden"
        style={{
          height: heroHeight + PARALLAX_TRAVEL,
          maskImage:
            "linear-gradient(transparent 0% 40%, black 65% 92%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(transparent 0% 40%, black 65% 92%, transparent 100%)",
        }}
      >
        {/* MapImage: object-position Y follows parallax offset (via CSS var);
              X is 50% by default, shifting to 100% at xl (xlgBreakpoint). */}
        <img
          src={mapBackground}
          alt="Visual of streets on a map"
          className={cn(
            "h-full w-full max-w-none object-cover",
            "[object-position:50%_var(--parallax-y)]",
            "xl:[object-position:100%_var(--parallax-y)]",
          )}
          style={{ "--parallax-y": `${roadBgOffset}px` } as React.CSSProperties}
        />
      </div>

      {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
      <div className="wrap-legacy">
        {/* HeroSection (inner, styled.div): text center on mobile, left at sm */}
        <div className="relative text-center sm:h-[33rem] sm:text-left md:h-[35rem] lg:h-[30rem]">
          {/* ParentChildWrapper: positions the family image + amoeba blob */}
          <div className="relative mt-[-22%] ml-[-18%] inline-block w-[135%] min-[431px]:mt-4 min-[431px]:ml-0 min-[431px]:w-[80%] sm:absolute sm:top-[10rem] sm:left-[10rem] sm:mt-0 sm:w-[23rem] md:top-[9rem] md:bottom-auto md:left-[14rem] md:w-[28rem] lg:top-[1rem] lg:left-[28rem] lg:w-[30rem] xl:left-[32rem] xl:w-[32rem]">
            <AmoebaLottie className="absolute top-1/2 left-1/2 inline-block w-full -translate-x-1/2 -translate-y-1/2" />
            {/* ParentChildImage */}
            <Picture
              src={familyDriver}
              loading="eager"
              alt="Parent and teenage child loading up a hatchback"
              className="relative m-[8%] h-auto w-[84%]"
            />
          </div>

          {/* HeroSubHeader */}
          <div className="pt-6 text-[5vw] font-bold text-[#65748F] sm:pt-16 sm:text-[18px] lg:pt-28 lg:text-[24px]">
            Know more. Worry less.
          </div>

          {/* HeroHeader (LandingPageHeaderH1 override) */}
          <h1
            className={cn(
              "font-display font-extrabold leading-[1.1em]",
              "mt-1 mb-8 text-center text-[9vw]",
              "min-[431px]:text-[9vw]",
              "sm:text-left sm:text-[40px]",
              "lg:text-[44px]",
              "xl:text-[48px]",
            )}
          >
            Connecting families
            <br />
            at every turn.
          </h1>
        </div>
      </div>
    </section>
  );
}
